import { MuxPauseIcon } from '@src/svgs/MuxPauseIcon';
import { MuxPlayIcon } from '@src/svgs/MuxPlayIcon';
import React from 'react';
import styled from 'styled-components';

interface RowPlayToggleProps {
  /** The row is the one being played, so the control offers pause. */
  isPlaying: boolean;
  /** What it offers while stopped — a Section plays *from* itself, a clip plays. */
  playLabel: string;
  testId?: string;
  onToggle: () => void;
  /**
   * Rest the play glyph on screen, on a dark badge, instead of hiding it until
   * hover — for a surface whose rows are seen once, by someone who has not
   * learnt that the frame plays. The badge keeps it legible on a light frame.
   * Pause still shows only on hover, so it does not cover the face playing.
   */
  alwaysVisible?: boolean;
}

/**
 * The play/pause over a row's frame. Hidden until the row is hovered (the
 * rename pencil's reveal idiom, which each row states in its own hover rule via
 * `[data-play-toggle]`), and held visible while that row plays so the pause
 * affordance is discoverable. `alwaysVisible` inverts that: play rests on a
 * badge so the frame reads as playable at a glance, and pause shows only on
 * hover — the main transcript player's idiom — so the face stays in view.
 *
 * Lives in `common/` because three features now put a player in a row frame —
 * the Reel Editor's Sections, the Add Clips picker's candidates, and the
 * speaker-identification modal's mapping rows — and a second copy of the
 * reveal idiom is a copy that drifts.
 */
export const RowPlayToggle: React.FC<RowPlayToggleProps> = ({
  isPlaying,
  playLabel,
  testId,
  onToggle,
  alwaysVisible = false,
}) => (
  <PlayToggle
    type="button"
    data-play-toggle
    data-testid={testId}
    aria-label={isPlaying ? 'Pause' : playLabel}
    $visible={isPlaying}
    $resting={alwaysVisible}
    onClick={e => {
      // Playing is not picking and not seeking: the row's own click does one of
      // those, and this control must not reach it (DS4).
      e.stopPropagation();
      onToggle();
      // A focused button would double-toggle on the space bar: its native
      // activation plus the control bar's document-level play/pause shortcut.
      e.currentTarget.blur();
    }}
  >
    <Glyph $badge={alwaysVisible}>
      {isPlaying ? (
        <MuxPauseIcon width="14" height="14" fillColor="currentColor" />
      ) : (
        <MuxPlayIcon width="14" height="14" fillColor="currentColor" />
      )}
    </Glyph>
  </PlayToggle>
);

const PlayToggle = styled.button<{ $visible: boolean; $resting: boolean }>`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background-color: transparent;
  color: var(--gray-100);
  cursor: pointer;
  filter: drop-shadow(0 1px 3px rgba(0, 0, 0, 0.85));
  // Hover-reveal surfaces hold the pause on screen while playing so it is
  // discoverable; a resting one already showed its play, and hides the pause
  // so the face playing underneath stays visible. The row's hover rule brings
  // it back, and it stays clickable (a tap still pauses) while transparent.
  opacity: ${props => {
    if (props.$resting) return props.$visible ? 0 : 1;
    return props.$visible ? 1 : 0;
  }};
  transition:
    opacity 0.15s ease,
    background-color 0.15s ease;

  &:hover {
    background-color: rgba(0, 0, 0, 0.4);
  }

  &:focus-visible {
    opacity: 1;
    background-color: rgba(0, 0, 0, 0.4);
    outline: 2px solid var(--primary);
    outline-offset: -2px;
  }
`;

const Glyph = styled.span<{ $badge: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  ${props =>
    props.$badge &&
    `
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background-color: rgba(0, 0, 0, 0.55);
  `}
`;

export default RowPlayToggle;
